// Stats — the fifth Publishing tab (session 36, artboards 01–05c). Keys mirror
// pl/appStats.ts.
//
// COPY FRAME. This is a SCREEN OF FACTS: numbers for the account owner's OWN posts,
// straight from the platforms' official APIs. Not one comparison ("better than"),
// forecast ("will reach") or piece of advice ("post at"), and not one number that
// belongs to somebody else. There is no filled action either: nothing changes here.
//
// RULE 8. The sentences that used to explain the numbers became pills: "no numbers
// yet" (a fresh post), "out of date" (numbers older than two sync cycles of that
// account), "sign in again" (the same words the Accounts tab uses). The label
// "every 2-3 days" replaced the sentence about YouTube's cycle. ASCII hyphen only.
//
// BANNED VOCABULARY. The first counter is called "Plays" (EN) and "Obejrzenia" (PL),
// because the platforms' own words ("views", "wyświetlenia", "odtworzenia") are words
// this product does not say in ANY locale (`scripts/forbidden_vocab_lint.py`, rule 4).
// MAPA 36 proposed a scoped lint exception for this one key; we do not open one — that
// is the gate owner's ruling, not an implementation package's.
export const appStats = {
  // The range switch; the value stays readable even when the control is inactive.
  range: {
    label: 'Stats range',
    d7: '7 days',
    d30: '30 days',
    all: 'All',
  },
  // The platform chip rail (36-R4) stands SEPARATELY from the account filter; the
  // account filter narrows down to the chosen platform. "All" clears the filter.
  platform: {
    label: 'Platform',
    all: 'All',
  },
  filter: {
    all: 'All accounts',
    title: 'Account',
    label: 'Account the numbers are for',
  },
  // The name of the series counting people on the account. WORKING TITLE: the owner's
  // ruling is pending and the alternatives went to them in the ST package receipt. ONE
  // key in PL and EN, so the change after that ruling is a single line (coordinator
  // ruling 6, brief of packages K / ST / P / I).
  series: {
    audience: 'Community',
  },
  // The chart card: series picker, unit, day readout and the screen-reader table.
  chart: {
    title: 'Chart',
    audienceUnit: 'people on the account, end of day',
    deltaUnit: 'daily change',
    // {series} = the name of the chosen series.
    label: 'Day by day: {series}',
    // A day we did NOT measure - in the screen-reader table instead of a number.
    noSnapshot: 'no snapshot',
    toggleSeries: 'Show or hide {account}',
    table: 'Chart table',
  },
  sort: {
    title: 'Sorting',
    label: 'Order of the post list',
    // {value} = the same menu entry, lower-cased.
    trigger: 'Sort: {value}',
    date: 'Publication date',
  },
  // Four counters. The same words carry the column headers, the 390 pairs and the
  // tiles inside the post dialog.
  counter: {
    c1: 'Plays',
    c2: 'Likes',
    c3: 'Comments',
    c4: 'Shares',
  },
  // Shortened headers inside the post dialog on 390; the full name stays for readers.
  counterShort: {
    c1: 'Plays',
    c2: 'Likes',
    c3: 'Com.',
    c4: 'Shares',
  },
  // ONE key, two variants: the short one next to an account total, the full one in the
  // post dialog and in the screen-reader label.
  updated: {
    short: 'updated {when}',
    full: 'last updated {when}',
  },
  // Rule 31: "today 14:20", "yesterday 18:00", "18 Sep, 18:00", another year "18.09.2025".
  // Eventually the product-wide format - for now it lives here (`components/stats/when.ts`).
  when: {
    today: 'today',
    yesterday: 'yesterday',
  },
  stale: 'out of date',
  reauth: {
    pill: 'sign in again',
    action: 'Connect again',
  },
  cycle: {
    // Non-breaking space before "days" so the label does not break in half.
    youtube: 'every 2-3 days',
  },
  pending: 'no numbers yet',
  // {platformLoc} comes from the existing `appAccounts.platformLoc` map.
  open: 'Open on {platformLoc}',
  post: {
    title: 'Post from {date}',
    daily: 'Day by day',
    day: 'Day',
    close: 'Close',
    // The row chevron on 390 opens the post sheet — this label is for readers only.
    openSheet: 'Show the numbers for the post from {date}',
  },
  more: 'Load more',
  listLabel: 'Posts with numbers',
  totalsLabel: 'Account totals in this range',
  empty: {
    noAccounts: {
      title: 'No connected accounts',
      action: 'Connect an account',
    },
    noPosts: {
      title: 'No publications',
      action: 'Schedule a publication',
    },
  },
  error: {
    title: 'The stats could not be loaded.',
    retry: 'Try again',
  },
  // 46-A (package PS): the track's ring towards its NEXT milestone. Copy from session A's
  // `copy/*.txt`; the first counter is "plays" (rule 4 - never the platforms' own word).
  // No explanatory sentences (rule 8): data on screen, the full text for screen readers.
  tracks: {
    title: 'Tracks',
    toNext: 'to {threshold}: {left}',
    aria: '{count} plays, {left} to {threshold}',
    ariaTop: '{count} plays',
    ariaHit: '{threshold} plays reached',
  },
  // The one-shot milestone card (frame 01b); the milestone number sits in the pill beside it.
  milestone: {
    label: 'plays on “{track}”',
    show: 'Show',
  },
  // The closed-month summary card (frame 01c).
  month: {
    total: 'Plays in {inMonth}',
    vs: {
      m1: 'compared with January', m2: 'compared with February', m3: 'compared with March', m4: 'compared with April', m5: 'compared with May', m6: 'compared with June',
      m7: 'compared with July', m8: 'compared with August', m9: 'compared with September', m10: 'compared with October', m11: 'compared with November', m12: 'compared with December',
    },
    record: 'best month so far',
    topTrack: 'Top track',
    quoted: '“{track}”',
    nextRing: 'To {threshold} on “{track}”',
    left: '{left} to go',
    name: {
      m1: 'January', m2: 'February', m3: 'March', m4: 'April', m5: 'May', m6: 'June',
      m7: 'July', m8: 'August', m9: 'September', m10: 'October', m11: 'November', m12: 'December',
    },
    in: {
      m1: 'January', m2: 'February', m3: 'March', m4: 'April', m5: 'May', m6: 'June',
      m7: 'July', m8: 'August', m9: 'September', m10: 'October', m11: 'November', m12: 'December',
    },
  },
  // The start ladder (frame 04): an account with no publication yet. The ring carries the
  // step's state; the state word is for screen readers only.
  ladder: {
    label: 'Publishing start',
    connected: 'Account connected',
    firstPost: 'First post',
    firstThreshold: 'First {threshold} plays on a track',
    done: 'done',
    todo: 'not yet',
  },
}
